import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { canWrite } from '../../rbac';
import { API_ORIGIN, certificateAPI, contentAPI } from '../../services/api';
import {
  normalizeCustomFormFields,
  certificateInputType,
  resolveCertificateValues,
  isCertificateFieldVisible,
  isCertificateFieldRequired,
} from '../../utils/certificateFields';
import { certificateDetailValue, orderedCertificateDetails } from '../../utils/certificateDetails';
import { downloadDocument } from '../../utils/documentDownload';
import DocumentPreviewTable from '../../components/DocumentPreviewTable';
import ApplicationDetailsList from '../../components/ApplicationDetailsList';
import CertificateViewPage from '../public/CertificateViewPage';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import {
  MAX_DOCUMENT_UPLOAD_BYTES,
  documentFileValidationError,
  isAllowedDocumentFile,
  normalizePhoneInput,
  isValidPhone,
  phoneMessage,
  requiredMessage,
  toEnglishDigits,
} from '../../utils/validation';
import useBackButtonClose from '../../hooks/useBackButtonClose';
import './Certificates.css';

const TYPE_LABELS = {
  birth: 'जन्म प्रमाणपत्र', death: 'मृत्यू प्रमाणपत्र', marriage: 'विवाह प्रमाणपत्र',
  residence: 'रहिवासी प्रमाणपत्र', income: 'उत्पन्न प्रमाणपत्र', caste: 'जात प्रमाणपत्र',
  domicile: 'अधिवास प्रमाणपत्र', no_objection: 'ना हरकत प्रमाणपत्र', land: 'जमीन प्रमाणपत्र',
  character: 'चारित्र्य प्रमाणपत्र', senior_citizen: 'ज्येष्ठ नागरिक प्रमाणपत्र',
  disability: 'दिव्यांग प्रमाणपत्र', bpl: 'दारिद्र्य रेषेखालील दाखला', migration: 'स्थलांतर प्रमाणपत्र',
  other: 'इतर प्रमाणपत्र',
};

const STATUS_LABELS = {
  submitted: 'सादर', under_review: 'पुनरावलोकनात', documents_requested: 'कागदपत्रे मागवली',
  approved: 'मंजूर', rejected: 'नाकारले', issued: 'जारी',
};

const STATUS_COLORS_MAP = {
  submitted: '#3498db', under_review: '#f39c12', documents_requested: '#e67e22',
  approved: '#27ae60', rejected: '#e74c3c', issued: '#2ecc71',
};

// Clean Marathi Labels dictionary for application data fields
const FIELD_LABELS_MARATHI = {
  groom_name: 'वराचे नाव (मराठी)',
  groom_name_en: 'वराचे नाव (इंग्रजी)',
  groom_dob: 'वराची जन्मतारीख',
  groom_aadhaar: 'वराचा १२ अंकी आधार क्रमांक',
  groom_address: 'वराचा संपूर्ण पत्ता',
  bride_name: 'वधूचे नाव (मराठी)',
  bride_name_en: 'वधूचे नाव (इंग्रजी)',
  bride_dob: 'वधूची जन्मतारीख',
  bride_aadhaar: 'वधूचा १२ अंकी आधार क्रमांक',
  bride_address: 'वधूचा संपूर्ण पत्ता',
  marriage_date: 'विवाह तारीख',
  marriage_place: 'विवाह ठिकाण',
  witness_1: 'साक्षीदार १',
  witness_2: 'साक्षीदार २',
  child_name: 'मुलाचे / मुलीचे नाव',
  date_of_birth: 'जन्म तारीख',
  place_of_birth: 'जन्म ठिकाण',
  father_name: 'वडिलांचे नाव',
  mother_name: 'आईचे नाव',
  gender: 'लिंग',
  hospital_name: 'रुग्णालयाचे नाव',
  deceased_name: 'मयत व्यक्तीचे नाव',
  date_of_death: 'मृत्यू तारीख',
  place_of_death: 'मृत्यू ठिकाण',
  cause_of_death: 'मृत्यूचे कारण',
  age_at_death: 'मृत्यू वेळचे वय',
  relation: 'मयताशी नाते',
  resident_since: 'रहिवासी कधीपासून',
  full_address: 'पूर्ण पत्ता',
  house_number: 'घर क्रमांक',
  ward_number: 'प्रभाग क्रमांक',
  annual_income: 'वार्षिक उत्पन्न (₹)',
  income_source: 'उत्पन्नाचे स्रोत',
  occupation: 'व्यवसाय',
  family_members: 'कुटुंबातील सदस्य संख्या',
  caste: 'जात',
  sub_caste: 'पोटजात',
  category: 'प्रवर्ग',
  father_caste_cert: 'वडिलांचा जात दाखला क्र.',
  survey_number: 'सर्वे / गट क्रमांक',
  area: 'क्षेत्रफळ',
  location: 'ठिकाण',
  ownership_type: 'मालकी प्रकार',
  noc_purpose: 'ना हरकत उद्देश',
  property_details: 'मालमत्ता तपशील',
  migration_date: 'स्थलांतर तारीख',
  migration_reason: 'स्थलांतराचे कारण',
  disability_type: 'दिव्यांगत्वाचा प्रकार',
  disability_percentage: 'दिव्यांगत्व टक्केवारी',
  medical_authority: 'वैद्यकीय प्राधिकरण',
  family_head_name: 'कुटुंब प्रमुखाचे नाव',
  ration_card_number: 'रेशन कार्ड क्रमांक',
  certificate_name: 'प्रमाणपत्राचे नाव',
  reason: 'कारण / तपशील',
};

const certificateCollator = new Intl.Collator('mr-IN', { numeric: true, sensitivity: 'base' });
const sortCertificateRows = (rows, sort) => [...rows].sort((a, b) => {
  const av = sort.key === 'submitted_at' ? (a.submitted_at || a.createdAt) : a?.[sort.key];
  const bv = sort.key === 'submitted_at' ? (b.submitted_at || b.createdAt) : b?.[sort.key];
  const result = sort.key === 'fee_amount' ? Number(av || 0) - Number(bv || 0) : certificateCollator.compare(String(av ?? ''), String(bv ?? ''));
  return sort.direction === 'ASC' ? result : -result;
});

const apiErrorMessage = (err, fallback) => {
  const data = err.response?.data;
  if (Array.isArray(data?.details) && data.details.length) {
    return data.details.map(item => item.message).join(', ');
  }
  return data?.error || fallback;
};

const EMPTY_CERTIFICATE_FORM = {
  certificate_type: '',
  applicant_name: '',
  applicant_phone: '',
  applicant_address: '',
  purpose: '',
  details: '',
};

const stripRequiredMark = label => String(label || '').replace(/\s*\*$/, '');

const normalizeDocumentRequirements = value => {
  let parsed = value;
  if (typeof value === 'string') {
    try { parsed = JSON.parse(value); } catch { parsed = value.split(/\r?\n|,/); }
  }
  if (!Array.isArray(parsed)) return [];
  return parsed.map(item => {
    if (typeof item === 'string') return { name: item.trim(), code: item.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, ''), document_role: '', is_required: true };
    return {
      name: String(item?.name || item?.document_name || item?.label || '').trim(),
      code: String(item?.code || item?.document_code || item?.name || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, ''),
      document_role: String(item?.document_role || item?.role || '').trim(),
      is_required: (item?.is_required ?? item?.required ?? true) !== false,
    };
  }).filter(item => item.name);
};

function AdminDynamicField({ field, value, onChange, error }) {
  const common = {
    value: field.type === 'checkbox' ? undefined : (value ?? ''),
    required: field.required,
    readOnly: field.readonly || field.type === 'calculated',
    placeholder: field.placeholder,
    minLength: field.minLength,
    maxLength: field.maxLength,
    min: field.min,
    max: field.max,
    pattern: field.pattern,
    'aria-invalid': Boolean(error),
  };
  const label = stripRequiredMark(field.label);
  return (
    <div className={`certificate-dynamic-field ${field.type === 'textarea' ? 'full-width' : ''}`}>
      {field.type === 'checkbox' ? (
        <label className="certificate-checkbox-field">
          <input type="checkbox" checked={Boolean(value)} onChange={event => onChange(event.target.checked)} />
          <span>{label}{field.required && <b> *</b>}</span>
        </label>
      ) : (
        <>
          <label>{label}{field.required && <b> *</b>}</label>
          {field.type === 'select' ? (
            <select {...common} onChange={event => onChange(event.target.value)}>
              <option value="">-- निवडा --</option>
              {(field.options || []).map(option => <option key={option} value={option}>{option}</option>)}
            </select>
          ) : field.type === 'textarea' ? (
            <textarea {...common} rows={3} onChange={event => onChange(event.target.value)} />
          ) : (
            <input
              {...common}
              type={certificateInputType(field.type)}
              inputMode={field.type === 'number' ? 'decimal' : field.inputMode}
              onKeyDown={event => { if (field.type === 'number' && ['e', 'E', '+', '-'].includes(event.key)) event.preventDefault(); }}
              onChange={event => {
                const next = field.type === 'number' ? toEnglishDigits(event.target.value) : event.target.value;
                if (field.type === 'number' && /[+-]/.test(next)) return;
                onChange(next);
              }}
            />
          )}
        </>
      )}
      {error && <span className="certificate-field-error" role="alert">{error}</span>}
      {!error && field.calculationHint && <small className="certificate-muted">{field.calculationHint}</small>}
    </div>
  );
}

export default function Certificates() {
  const { tenant, user } = useAuth();
  const slug = tenant?.slug;
  const canWriteThis = canWrite(user?.role, 'certificates', user);
  const [certs, setCerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [detailModal, setDetailModal] = useState(null); // full application detail object
  const [previewCertificateId, setPreviewCertificateId] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [pageSize, setPageSize] = useState(20);
  const [sort, setSort] = useState({ key: 'submitted_at', direction: 'DESC' });
  const [exporting, setExporting] = useState(false);
  const [remarks, setRemarks] = useState('');
  const [correctionMode, setCorrectionMode] = useState(false);
  const [correctionData, setCorrectionData] = useState({});
  const [volumeNo, setVolumeNo] = useState('');
  const [serialNo, setSerialNo] = useState('');
  const [issueDate, setIssueDate] = useState('');
  const [updating, setUpdating] = useState(false);
  const [serviceFees, setServiceFees] = useState([]);
  const [serviceConfigLoading, setServiceConfigLoading] = useState(true);
  const [dynamicData, setDynamicData] = useState({});
  const [documents, setDocuments] = useState({});
  const [formErrors, setFormErrors] = useState({});
  const [submittingApplication, setSubmittingApplication] = useState(false);
  const [certificateStep, setCertificateStep] = useState(0);
  const documentInputRefs = useRef({});
  const certificateFormTopRef = useRef(null);
  const previewObjectUrls = useRef(new Set());
  const pendingUploadReceipts = useRef(new Set());

  const revokePendingReceipt = useCallback(receipt => {
    if (!receipt) return;
    pendingUploadReceipts.current.delete(receipt);
    certificateAPI.revokePendingUpload(slug, receipt).catch(() => {});
  }, [slug]);

  const revokeAllPendingReceipts = useCallback(() => {
    const receipts = [...pendingUploadReceipts.current];
    pendingUploadReceipts.current.clear();
    receipts.forEach(receipt => certificateAPI.revokePendingUpload(slug, receipt).catch(() => {}));
  }, [slug]);

  const clearDocumentPreviews = useCallback(() => {
    previewObjectUrls.current.forEach(url => URL.revokeObjectURL(url));
    previewObjectUrls.current.clear();
  }, []);

  useBackButtonClose(showForm || !!detailModal, () => {
    if (previewCertificateId) {
      setPreviewCertificateId(null);
      return;
    }
    revokeAllPendingReceipts();
    clearDocumentPreviews();
    setShowForm(false);
    setDetailModal(null);
  });
  const [totalCount, setTotalCount] = useState(0);

  const handleSearchChange = (val) => { setSearch(val); setPage(1); };
  const handleTypeFilterChange = (val) => { setTypeFilter(val); setPage(1); };
  const handleStatusFilterChange = (val) => { setStatusFilter(val); setPage(1); };

  const certTypes = useMemo(() => serviceFees
    .filter(fee => fee?.is_active !== false && (fee?.service_provider || 'grampanchayat') === 'grampanchayat')
    .map(fee => fee.service_type)
    .filter(Boolean), [serviceFees]);
  const filterCertTypes = useMemo(
    () => [...new Set([...Object.keys(TYPE_LABELS), ...certTypes])],
    [certTypes],
  );

  const [formData, setFormData] = useState(EMPTY_CERTIFICATE_FORM);
  const selectedFee = useMemo(
    () => serviceFees.find(fee => fee.service_type === formData.certificate_type),
    [serviceFees, formData.certificate_type],
  );
  const dynamicFormSections = useMemo(
    () => normalizeCustomFormFields(selectedFee?.custom_form_fields),
    [selectedFee],
  );
  const requiredDocuments = useMemo(
    () => normalizeDocumentRequirements(selectedFee?.required_documents),
    [selectedFee],
  );
  const resolvedDynamicData = useMemo(
    () => resolveCertificateValues(dynamicFormSections, dynamicData),
    [dynamicFormSections, dynamicData],
  );
  const applicationSteps = useMemo(() => [
    { id: 'applicant', title: 'अर्जदार माहिती', icon: '1' },
    ...dynamicFormSections.map((section, index) => ({
      id: section.section_id || `section-${index}`,
      title: stripRequiredMark(section.section_title) || `विभाग ${index + 1}`,
      icon: String(index + 2),
      section,
    })),
    { id: 'documents', title: 'कागदपत्रे', icon: String(dynamicFormSections.length + 2) },
    { id: 'review', title: 'तपशील व सादरीकरण', icon: String(dynamicFormSections.length + 3) },
  ], [dynamicFormSections]);

  const resetApplicationForm = useCallback((nextType = certTypes[0] || '') => {
    revokeAllPendingReceipts();
    clearDocumentPreviews();
    setFormData({ ...EMPTY_CERTIFICATE_FORM, certificate_type: nextType });
    setDynamicData({});
    setDocuments({});
    setFormErrors({});
    setCertificateStep(0);
  }, [certTypes, clearDocumentPreviews, revokeAllPendingReceipts]);

  useEffect(() => () => {
    revokeAllPendingReceipts();
    clearDocumentPreviews();
  }, [clearDocumentPreviews, revokeAllPendingReceipts]);

  useEffect(() => {
    if (!slug) return;
    setServiceConfigLoading(true);
    contentAPI.getFees(slug)
      .then(response => setServiceFees(response.data.fees || []))
      .catch(() => {
        setServiceFees([]);
        setError('प्रमाणपत्र फॉर्म संरचना लोड करता आली नाही.');
      })
      .finally(() => setServiceConfigLoading(false));
  }, [slug]);

  useEffect(() => {
    if (!formData.certificate_type && certTypes.length) {
      setFormData(current => ({ ...current, certificate_type: certTypes[0] }));
    }
  }, [certTypes, formData.certificate_type]);

  useEffect(() => {
    if (certificateStep >= applicationSteps.length) setCertificateStep(0);
  }, [applicationSteps.length, certificateStep]);

  const loadData = useCallback(async () => {
    if (!slug) return;
    setLoading(true);
    try {
      const params = { page, limit: pageSize, sort_by: sort.key, sort_order: sort.direction };
      if (search) params.search = search;
      if (typeFilter) params.certificate_type = typeFilter;
      if (statusFilter) params.status = statusFilter;
      const res = await certificateAPI.getAll(slug, params);
      setCerts(sortCertificateRows(res.data.applications || [], sort));
      setTotalPages(res.data.pagination?.totalPages || 1);
      setTotalCount(res.data.pagination?.total || 0);
    } catch (err) { setError('डेटा लोड करण्यात अयशस्वी'); }
    finally { setLoading(false); }
  }, [slug, page, pageSize, search, typeFilter, statusFilter, sort]);

  useEffect(() => { loadData(); }, [loadData]);

  const toggleSort = key => { setSort(current => ({ key, direction: current.key === key && current.direction === 'ASC' ? 'DESC' : 'ASC' })); setPage(1); };
  const exportCsv = async () => {
    if (!slug || exporting) return; setExporting(true); setError('');
    try {
      const params = { page: 1, limit: Math.max(totalCount, pageSize), sort_by: sort.key, sort_order: sort.direction };
      if (search) params.search = search; if (typeFilter) params.certificate_type = typeFilter; if (statusFilter) params.status = statusFilter;
      const { data } = await certificateAPI.getAll(slug, params); const rows = sortCertificateRows(data.applications || [], sort);
      const cell = value => `"${String(value ?? '').replace(/"/g, '""')}"`;
      const csv = [['UID','प्रकार','अर्जदार','स्थिती','शुल्क','तारीख'], ...rows.map(c => [c.application_uid,TYPE_LABELS[c.certificate_type] || c.certificate_type,c.applicant?.full_name || c.subject_name,STATUS_LABELS[c.status] || c.status,c.fee_amount || 0,new Date(c.submitted_at || c.createdAt).toLocaleDateString('mr-IN')])].map(row => row.map(cell).join(',')).join('\n');
      const url = URL.createObjectURL(new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8' })); const link = document.createElement('a'); link.href = url; link.download = `certificates-${new Date().toISOString().slice(0,10)}.csv`; link.click(); URL.revokeObjectURL(url);
    } catch { setError('निर्यात तयार करता आली नाही.'); } finally { setExporting(false); }
  };

  const configuredFieldError = (field) => {
    if (!isCertificateFieldVisible(field, resolvedDynamicData)) return '';
    if (field.type === 'calculated' && field.calculation
      && (!resolvedDynamicData[field.calculation.date_field] || !resolvedDynamicData[field.calculation.as_of_field])) return '';
    const rawValue = resolvedDynamicData[field.key];
    const value = typeof rawValue === 'boolean' ? rawValue : String(rawValue ?? '').trim();
    const missing = field.type === 'checkbox' ? value !== true : value === '';
    const message = field.errorMessage || requiredMessage(stripRequiredMark(field.label));
    if (isCertificateFieldRequired(field, resolvedDynamicData) && missing) return message;
    if (missing) return '';
    const text = String(value);
    if (field.minLength != null && text.length < Number(field.minLength)) return message;
    if (field.maxLength != null && text.length > Number(field.maxLength)) return message;
    if (field.type === 'number') {
      const numeric = Number(text);
      if (/[+-]/.test(text) || !/^\d+(\.\d+)?$/.test(text)
        || (field.min != null && numeric < Number(field.min))
        || (field.max != null && numeric > Number(field.max))) return message;
    }
    if (field.pattern) {
      try {
        if (!new RegExp(field.pattern).test(text)) return message;
      } catch {
        return `${field.label} साठी संरचित पडताळणी नियम चुकीचा आहे.`;
      }
    }
    return '';
  };

  const validateApplicationStep = (step) => {
    const nextErrors = {};
    if (step.id === 'applicant') {
      if (!formData.certificate_type || !selectedFee) nextErrors.certificate_type = 'प्रमाणपत्र प्रकार निवडा.';
      if (!formData.applicant_name.trim()) nextErrors.applicant_name = requiredMessage('अर्जदाराचे नाव');
      if (!isValidPhone(formData.applicant_phone)) nextErrors.applicant_phone = phoneMessage;
    } else if (step.section) {
      (step.section.fields || []).forEach(field => {
        const fieldError = configuredFieldError(field);
        if (fieldError) nextErrors[field.key] = fieldError;
      });
    } else if (step.id === 'documents') {
      const missingDocuments = requiredDocuments.filter(doc => doc.is_required !== false && !documents[doc.name]?.url);
      if (missingDocuments.length) {
        nextErrors.documents = `आवश्यक कागदपत्रे अपलोड करा: ${missingDocuments.map(doc => doc.name).join(', ')}`;
      }
    }
    return nextErrors;
  };

  const goToCertificateStep = (nextStep) => {
    if (nextStep > certificateStep) {
      const stepErrors = validateApplicationStep(applicationSteps[certificateStep]);
      if (Object.keys(stepErrors).length) {
        setFormErrors(current => ({ ...current, ...stepErrors }));
        return;
      }
    }
    setCertificateStep(Math.max(0, Math.min(nextStep, applicationSteps.length - 1)));
    requestAnimationFrame(() => certificateFormTopRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(''); setSuccess('');
    const allErrors = applicationSteps.reduce(
      (errors, step) => ({ ...errors, ...validateApplicationStep(step) }),
      {},
    );
    if (Object.keys(allErrors).length) {
      setFormErrors(allErrors);
      const firstInvalidStep = applicationSteps.findIndex(step => Object.keys(validateApplicationStep(step)).length);
      if (firstInvalidStep >= 0) {
        setCertificateStep(firstInvalidStep);
        requestAnimationFrame(() => certificateFormTopRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
      }
      return;
    }

    setSubmittingApplication(true);
    try {
      const documentList = Object.entries(documents)
        .filter(([, document]) => document?.url)
        .map(([name, document]) => ({
          name,
          code: requiredDocuments.find(item => item.name === name)?.code,
          label: name,
          uploadReceipt: document.uploadReceipt,
          originalName: document.originalName,
        }));
      const subjectName = formData.certificate_type === 'marriage'
        && dynamicData.groom_name && dynamicData.bride_name
        ? `${dynamicData.groom_name} व ${dynamicData.bride_name}`
        : formData.applicant_name.trim();
      await certificateAPI.create(slug, {
        certificate_type: formData.certificate_type,
        subject_name: subjectName,
        application_data: {
          applicant_name: formData.applicant_name.trim(),
          applicant_phone: normalizePhoneInput(formData.applicant_phone),
          applicant_address: formData.applicant_address.trim() || null,
          purpose: formData.purpose.trim() || null,
          details: formData.details.trim() || null,
          ...resolvedDynamicData,
          custom_fields: resolvedDynamicData,
          submitted_by_admin: true,
        },
        documents: documentList,
      });
      pendingUploadReceipts.current.clear();
      setSuccess('प्रमाणपत्र अर्ज यशस्वीरीत्या सादर केला!');
      setShowForm(false);
      resetApplicationForm();
      loadData();
    } catch (err) { setError(apiErrorMessage(err, 'सादर करण्यात अयशस्वी')); }
    finally { setSubmittingApplication(false); }
  };

  const handleConfiguredDocumentUpload = async (documentName, file) => {
    if (!file) return;
    if (!isAllowedDocumentFile(file)) {
      const uploadError = documentFileValidationError(file) === 'size'
        ? `फाईल ${MAX_DOCUMENT_UPLOAD_BYTES / (1024 * 1024)}MB किंवा कमी असावी.`
        : 'केवळ JPG, PNG, WebP किंवा PDF फाईल अपलोड करा.';
      setDocuments(current => ({
        ...current,
        [documentName]: { ...current[documentName], error: uploadError },
      }));
      return;
    }
    const previousDocument = documents[documentName];
    setDocuments(current => ({ ...current, [documentName]: { ...current[documentName], uploading: true } }));
    try {
      const upload = new FormData();
      upload.append('document', file);
      const response = await certificateAPI.uploadDoc(slug, upload);
      const previewUrl = URL.createObjectURL(file);
      previewObjectUrls.current.add(previewUrl);
      pendingUploadReceipts.current.add(response.data.uploadReceipt);
      revokePendingReceipt(previousDocument?.uploadReceipt);
      setDocuments(current => {
        const previousPreview = current[documentName]?.previewUrl;
        if (previousPreview) {
          URL.revokeObjectURL(previousPreview);
          previewObjectUrls.current.delete(previousPreview);
        }
        return {
          ...current,
          [documentName]: {
          url: response.data.url,
          uploadReceipt: response.data.uploadReceipt,
          mimeType: response.data.mimeType,
          previewUrl,
          originalName: response.data.originalName || file.name,
          uploading: false,
          },
        };
      });
      setFormErrors(current => ({ ...current, documents: '' }));
    } catch (err) {
      setDocuments(current => ({
        ...current,
        [documentName]: {
          ...current[documentName],
          uploading: false,
          error: err.response?.data?.error || 'कागदपत्र अपलोड करता आले नाही.',
        },
      }));
    }
  };

  const removeConfiguredDocument = documentName => {
    const document = documents[documentName];
    revokePendingReceipt(document?.uploadReceipt);
    if (document?.previewUrl) {
      URL.revokeObjectURL(document.previewUrl);
      previewObjectUrls.current.delete(document.previewUrl);
    }
    setDocuments(current => {
      const next = { ...current };
      delete next[documentName];
      return next;
    });
    if (documentInputRefs.current[documentName]) documentInputRefs.current[documentName].value = '';
  };

  // Parse application_data (could be JSON string or object)
  const parseAppData = (d) => {
    if (!d) return {};
    if (typeof d === 'string') { try { return JSON.parse(d); } catch { return {}; } }
    return d;
  };

  const openDetail = async (certId) => {
    setDetailLoading(true);
    setRemarks('');
    try {
      const res = await certificateAPI.getById(slug, certId);
      const app = res.data.application;
      setDetailModal(app);
      const appData = parseAppData(app?.application_data);
      setCorrectionData(appData);
      setCorrectionMode(false);
      const currentYear = new Date().getFullYear();
      setVolumeNo(appData?.volume_no || String(currentYear));
      setSerialNo(appData?.serial_no || String(app?.id || '3'));
      setIssueDate(appData?.registration_date || app?.issued_at?.slice(0, 10) || new Date().toISOString().slice(0, 10));
    } catch (err) {
      setError('अर्ज तपशील लोड करण्यात अयशस्वी');
    } finally {
      setDetailLoading(false);
    }
  };

  const handleStatusUpdate = async (status) => {
    if (!detailModal) return;
    setError(''); setSuccess(''); setUpdating(true);
    try {
      const payload = {
        status,
        remarks,
        volume_no: volumeNo,
        serial_no: serialNo,
        issued_at: issueDate,
        origin: window.location.origin,
      };
      const res = await certificateAPI.updateStatus(slug, detailModal.id, payload);
      setSuccess(`स्थिती "${STATUS_LABELS[status] || status}" मध्ये अद्ययावत केली`);
      if (res.data?.application) {
        setDetailModal(res.data.application);
      } else {
        openDetail(detailModal.id);
      }
      loadData();
    } catch (err) { setError(err.response?.data?.error || 'अद्ययावत करण्यात अयशस्वी'); }
    finally { setUpdating(false); }
  };

  const handleReissue = async () => {
    if (!detailModal) return;
    const reason = window.prompt('पुनर्जारी करण्याचे स्पष्ट कारण लिहा (किमान ५ अक्षरे):');
    if (!reason) return;
    setError(''); setSuccess(''); setUpdating(true);
    try {
      const response = await certificateAPI.reissue(slug, detailModal.id, {
        reason,
        volume_no: volumeNo,
        serial_no: serialNo,
        issued_at: issueDate,
        origin: window.location.origin,
      });
      setSuccess(`प्रमाणपत्राची नवीन अपरिवर्तनीय आवृत्ती तयार झाली (Revision ${response.data?.issuance?.revision || ''}).`);
      if (response.data?.application) setDetailModal(response.data.application);
      await loadData();
    } catch (err) {
      setError(err.response?.data?.error || 'प्रमाणपत्र पुनर्जारी करता आले नाही.');
    } finally { setUpdating(false); }
  };

  const handleCertificateDataCorrection = async () => {
    if (!detailModal) return;
    const reason = window.prompt('दुरुस्तीचे स्पष्ट कारण लिहा (किमान ५ अक्षरे):');
    if (!reason) return;
    setError(''); setSuccess(''); setUpdating(true);
    try {
      const detailSections = normalizeCustomFormFields(
        serviceFees.find(fee => fee.service_type === detailModal.certificate_type)?.custom_form_fields,
      );
      const resolved = resolveCertificateValues(detailSections, correctionData);
      const response = await certificateAPI.correctCertificateData(slug, detailModal.id, {
        reason,
        application_data: resolved,
      });
      setDetailModal(response.data.application);
      setCorrectionData(parseAppData(response.data.application?.application_data));
      setCorrectionMode(false);
      setSuccess(response.data.reissue_required
        ? 'तपशील दुरुस्त झाले. नागरिकाला नवीन माहिती दिसण्यासाठी प्रमाणपत्र पुनर्जारी करा.'
        : 'प्रमाणपत्र अर्जाचा तपशील दुरुस्त झाला.');
      await loadData();
    } catch (err) {
      setError(apiErrorMessage(err, 'प्रमाणपत्राचा तपशील दुरुस्त करता आला नाही.'));
    } finally { setUpdating(false); }
  };

  // Parse documents array
  const parseDocs = (d) => {
    if (!d) return [];
    if (typeof d === 'string') { try { return JSON.parse(d); } catch { return []; } }
    return Array.isArray(d) ? d : [];
  };

  const getDocUrl = (url) => {
    if (!url) return '#';
    if (url.startsWith('http')) return url;
    return `${API_ORIGIN}${url.startsWith('/') ? '' : '/'}${url}`;
  };

  // ── Render Dedicated Full-Page Workspace View for Application Details ──
  if (detailLoading || detailModal) {
    if (detailLoading) {
      return (
        <div style={{ padding: '60px 20px', textAlign: 'center', background: '#fff', borderRadius: 14 }}>
          <div style={{ fontSize: '2rem', marginBottom: 12 }}>⏳</div>
          <h3 style={{ color: '#1a237e', margin: 0 }}>अर्ज तपशील लोड होत आहे...</h3>
        </div>
      );
    }

    const app = detailModal;
    const appData = parseAppData(app.application_data);
    const detailSections = normalizeCustomFormFields(
      serviceFees.find(fee => fee.service_type === app.certificate_type)?.custom_form_fields,
    );
    const sColor = STATUS_COLORS_MAP[app.status] || '#888';
    const applicant = app.applicant;

    if (previewCertificateId) {
      return (
        <CertificateViewPage
          embedded
          certId={previewCertificateId}
          tenantSlug={slug}
          onClose={() => setPreviewCertificateId(null)}
        />
      );
    }

    return (
      <div>
        <div className="certificate-detail-workspace">
          
          {/* Dedicated Page Header Navigation */}
          <div className="detail-top-nav">
            <button className="detail-back-btn" onClick={() => setDetailModal(null)}>
              ← सर्व प्रमाणपत्रांकडे परत जा
            </button>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <span style={{ fontFamily: 'monospace', background: '#e0e7ff', color: '#3730a3', padding: '6px 14px', borderRadius: 8, fontSize: '0.9rem', fontWeight: 800 }}>
                {app.application_uid}
              </span>
              <span style={{ background: sColor, color: '#fff', padding: '6px 14px', borderRadius: 20, fontSize: '0.8rem', fontWeight: 800, textTransform: 'uppercase' }}>
                {STATUS_LABELS[app.status] || app.status}
              </span>
            </div>
          </div>

          {/* Banner Summary */}
          <div style={{
            background: 'linear-gradient(135deg, #1a237e 0%, #283593 100%)', color: '#fff',
            borderRadius: 14, padding: '20px 24px', marginBottom: 24, boxShadow: '0 4px 16px rgba(26,35,126,0.15)',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16
          }}>
            <div>
              <h2 style={{ margin: 0, fontSize: '1.4rem', fontWeight: 800 }}>
                {TYPE_LABELS[app.certificate_type] || app.certificate_type?.replace(/_/g, ' ')}
              </h2>
              <p style={{ margin: '6px 0 0', opacity: 0.9, fontSize: '0.88rem' }}>
                अर्जदार: <strong>{applicant?.full_name || app.subject_name || '—'}</strong> ·
                तारीख: <strong>{new Date(app.submitted_at || app.createdAt).toLocaleDateString('mr-IN', { day: 'numeric', month: 'long', year: 'numeric' })}</strong>
                {app.fee_amount > 0 && <span> · शासकीय शुल्क: <strong>₹{app.fee_amount}</strong></span>}
              </p>
            </div>
            {app.certificate_number && (
              <div style={{ background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(4px)', padding: '10px 18px', borderRadius: 10, textAlign: 'right' }}>
                <span style={{ fontSize: '0.72rem', textTransform: 'uppercase', opacity: 0.8, fontWeight: 700 }}>प्रमाणपत्र नोंदणी क्र.</span>
                <div style={{ fontFamily: 'monospace', fontSize: '1.05rem', fontWeight: 800 }}>{app.certificate_number}</div>
              </div>
            )}
          </div>

          {success && <div className="alert alert-success" style={{ marginBottom: 18 }}>{success}</div>}
          {error && <div className="alert alert-error" style={{ marginBottom: 18 }}>{error}</div>}

          {/* 2-Column Responsive Workspace Grid */}
          <div className="detail-main-grid">
            
            {/* Left Column: Details & Documents */}
            <div>
              
              {/* CARD 1: 👤 अर्जदार माहिती (Applicant Info) */}
              <div className="detail-card">
                <h3 className="detail-card-title">👤 अर्जदाराची नोंदणीकृत माहिती (Applicant Profile)</h3>
                <ApplicationDetailsList entries={[
                  { key: 'name', label: 'संपूर्ण नाव', value: applicant?.full_name || app.subject_name || '—' },
                  { key: 'phone', label: 'मोबाईल क्रमांक', value: applicant?.phone || appData?.applicant_phone || '—' },
                  { key: 'email', label: 'ईमेल आयडी', value: applicant?.email || '—' },
                  { key: 'aadhaar', label: 'आधार क्रमांक', value: applicant?.aadhaar_number || '—' },
                  { key: 'address', label: 'संपूर्ण पत्ता', value: applicant?.address || appData?.applicant_address || appData?.full_address || '—' },
                ]} />
              </div>

              {/* CARD 2: 📋 अर्ज तपशील (Structured Application Fields) */}
              <div className="detail-card">
                <h3 className="detail-card-title">📋 प्रमाणपत्राचा सविस्तर तपशील (Application Details)</h3>
                <ApplicationDetailsList entries={orderedCertificateDetails(appData, detailSections)
                  .filter(detail => detail.value && !new Set([
                    'applicant_address', 'applicant_phone', 'full_address', 'custom_fields',
                    'groom_photo_url', 'bride_photo_url', 'groom_photo', 'bride_photo',
                    'husband_photo', 'wife_photo', 'volume_no', 'serial_no', 'registration_date',
                  ]).has(detail.key))
                  .map(detail => ({
                    key: detail.key,
                    label: detail.label || FIELD_LABELS_MARATHI[detail.key],
                    value: certificateDetailValue(detail.value),
                  }))} />
                {canWriteThis && !correctionMode && (
                  <button
                    type="button"
                    onClick={() => setCorrectionMode(true)}
                    style={{ marginTop: 12, padding: '8px 12px', color: '#1d4ed8', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 7, fontWeight: 700, cursor: 'pointer' }}
                  >
                    ✎ प्रमाणपत्र फील्ड दुरुस्त करा
                  </button>
                )}
                {canWriteThis && correctionMode && (
                  <div style={{ marginTop: 14, padding: 14, border: '1px solid #93c5fd', borderRadius: 10, background: '#f8fbff' }}>
                    <div style={{ marginBottom: 10, color: '#1e40af', fontSize: '0.78rem', fontWeight: 800 }}>
                      दुरुस्ती audit log मध्ये नोंदवली जाईल. आधी जारी केलेली PDF बदलणार नाही; आवश्यक असल्यास नवीन revision पुनर्जारी करा.
                    </div>
                    {detailSections.map((section, sectionIndex) => (
                      <div key={section.section_id || sectionIndex} style={{ marginBottom: 13 }}>
                        <strong style={{ display: 'block', marginBottom: 8, color: '#334155', fontSize: '0.82rem' }}>{stripRequiredMark(section.section_title) || `विभाग ${sectionIndex + 1}`}</strong>
                        <div className="certificate-dynamic-grid">
                          {(section.fields || []).map(field => (
                            <AdminDynamicField
                              key={field.key}
                              field={field}
                              value={resolveCertificateValues(detailSections, correctionData)[field.key]}
                              onChange={value => setCorrectionData(current => ({ ...current, [field.key]: value }))}
                            />
                          ))}
                        </div>
                      </div>
                    ))}
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button type="button" disabled={updating} onClick={handleCertificateDataCorrection} style={{ padding: '9px 13px', color: '#fff', background: '#1d4ed8', border: 0, borderRadius: 7, fontWeight: 800, cursor: 'pointer' }}>दुरुस्ती जतन करा</button>
                      <button type="button" disabled={updating} onClick={() => { setCorrectionData(appData); setCorrectionMode(false); }} style={{ padding: '9px 13px', color: '#475569', background: '#fff', border: '1px solid #cbd5e1', borderRadius: 7, fontWeight: 700, cursor: 'pointer' }}>रद्द</button>
                    </div>
                  </div>
                )}
              </div>

              {/* CARD 3: 📎 जोडलेली कागदपत्रे (Attached Documents) */}
              {(() => {
                const baseDocs = parseDocs(app.documents);
                const docUrls = new Set(baseDocs.map(d => (typeof d === 'string' ? d : d.url)));
                const allDocs = [...baseDocs];
                if (appData.groom_photo_url && !docUrls.has(appData.groom_photo_url)) {
                  allDocs.push({ label: 'वर पासपोर्ट आकार फोटो (Groom Photo)', url: appData.groom_photo_url, originalName: 'groom_photo.jpg' });
                }
                if (appData.bride_photo_url && !docUrls.has(appData.bride_photo_url)) {
                  allDocs.push({ label: 'वधू पासपोर्ट आकार फोटो (Bride Photo)', url: appData.bride_photo_url, originalName: 'bride_photo.jpg' });
                }

                return (
                  <div className="detail-card">
                    <h3 className="detail-card-title">📎 जोडलेली कागदपत्रे व पुरावे ({allDocs.length})</h3>
                    {allDocs.length > 0 ? (
                      <DocumentPreviewTable documents={allDocs.map((doc, i) => ({
                        key: `${doc.url || 'document'}-${i}`,
                        name: doc.label || doc.name || doc.originalName || `कागदपत्र ${i + 1}`,
                        fileName: doc.originalName || doc.url?.split('/').pop() || 'फाइल',
                        url: getDocUrl(doc.url),
                      }))} />
                    ) : (
                      <p style={{ margin: 0, color: '#94a3b8', fontSize: '0.88rem' }}>कोणतीही अतिरिक्त कागदपत्रे जोडलेली नाहीत.</p>
                    )}
                  </div>
                );
              })()}

            </div>

            {/* Right Column: Admin Actions, Status, and Certificate Generation */}
            <div>
              
              {/* CARD 4: ⚙️ प्रशासन कृती व नियंत्रण (Admin Actions Card) */}
              <div className="detail-card" style={{ background: '#f0fdf4', border: '1.5px solid #86efac' }}>
                <h3 className="detail-card-title" style={{ color: '#166534' }}>⚙️ प्रशासन कृती व मंजुरी नियंत्रण</h3>

                {/* Fee Status Summary */}
                <div style={{ background: '#fff', borderRadius: 10, padding: 14, marginBottom: 16, border: '1px solid #bbf7d0' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <span style={{ fontSize: '0.82rem', color: '#475569', fontWeight: 600 }}>शासकीय शुल्क:</span>
                    <strong style={{ fontSize: '1rem', color: '#0f172a' }}>₹{app.fee_amount || 0}</strong>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: '0.82rem', color: '#475569', fontWeight: 600 }}>भरणा स्थिती:</span>
                    <span style={{ padding: '3px 10px', borderRadius: 12, fontSize: '0.78rem', fontWeight: 800, background: app.fee_paid ? '#dcfce7' : '#fee2e2', color: app.fee_paid ? '#15803d' : '#991b1b' }}>
                      {app.fee_paid ? '✅ भरणा पूर्ण' : '❌ बाकी'}
                    </span>
                  </div>
                  {canWriteThis && (
                    <button
                      type="button"
                      style={{ marginTop: 10, width: '100%', padding: '7px 12px', background: app.fee_paid ? '#fff' : '#16a34a', border: app.fee_paid ? '1px solid #cbd5e1' : 'none', borderRadius: 6, color: app.fee_paid ? '#334155' : '#fff', fontWeight: 700, fontSize: '0.8rem', cursor: 'pointer' }}
                      onClick={async () => {
                        try {
                          const newPaid = !app.fee_paid;
                          const mode = newPaid ? (prompt('भरणा पद्धती (cash/online/bank_transfer/waived):', 'cash') || 'cash') : null;
                          await certificateAPI.updatePayment(slug, app.id, { fee_paid: newPaid, payment_mode: mode });
                          setDetailModal({ ...app, fee_paid: newPaid, payment_mode: mode });
                          setSuccess('भरणा स्थिती अद्ययावत केली');
                        } catch { setError('भरणा अद्ययावत करण्यात अयशस्वी'); }
                      }}
                    >
                      {app.fee_paid ? '↩ बाकी म्हणून चिन्हांकित करा' : '💰 भरणा प्राप्त झाल्याचे नोंदवा'}
                    </button>
                  )}
                </div>

                {/* Status Update Form Controls */}
                {canWriteThis && !['issued', 'rejected'].includes(app.status) && (
                  <div style={{ marginBottom: 16 }}>
                    
                    {/* Volume & Serial Number Input Box */}
                    <div style={{ background: '#fff', borderRadius: 10, padding: 14, marginBottom: 14, border: '1px solid #93c5fd' }}>
                      <div style={{ fontSize: '0.82rem', fontWeight: 800, color: '#1d4ed8', marginBottom: 10 }}>
                        📜 नोंदवही नोंद व अनुक्रमांक (Register Entry)
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
                        <div>
                          <label style={{ display: 'block', fontSize: '0.74rem', fontWeight: 700, color: '#475569', marginBottom: 3 }}>
                            नोंदवही खंड / Volume (वर्ष) *
                          </label>
                          <input
                            type="text"
                            value={volumeNo}
                            onChange={e => setVolumeNo(e.target.value)}
                            placeholder="उदा. 2026"
                            style={{ width: '100%', boxSizing: 'border-box', padding: '6px 10px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: '0.85rem' }}
                          />
                        </div>
                        <div>
                          <label style={{ display: 'block', fontSize: '0.74rem', fontWeight: 700, color: '#475569', marginBottom: 3 }}>
                            नोंदणी अनुक्रमांक / Serial No *
                          </label>
                          <input
                            type="text"
                            value={serialNo}
                            onChange={e => setSerialNo(e.target.value)}
                            placeholder="उदा. 3"
                            style={{ width: '100%', boxSizing: 'border-box', padding: '6px 10px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: '0.85rem' }}
                          />
                        </div>
                      </div>
                      <div>
                        <label style={{ display: 'block', fontSize: '0.74rem', fontWeight: 700, color: '#475569', marginBottom: 3 }}>
                          नोंदणी / जारी तारीख (Registration Date) *
                        </label>
                        <input
                          type="date"
                          value={issueDate}
                          onChange={e => setIssueDate(e.target.value)}
                          style={{ width: '100%', boxSizing: 'border-box', padding: '6px 10px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: '0.85rem' }}
                        />
                      </div>
                    </div>

                    <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 700, color: '#334155', marginBottom: 6 }}>शेरा / पुनरावलोकन नोंद</label>
                    <textarea
                      rows={3}
                      value={remarks}
                      onChange={e => setRemarks(e.target.value)}
                      placeholder="नागरिकासाठी किंवा ग्रामसेवक शेऱ्यासाठी नोंद लिहा..."
                      style={{ width: '100%', boxSizing: 'border-box', borderRadius: 8, border: '1px solid #cbd5e1', padding: '8px 12px', fontSize: '0.86rem', fontFamily: 'inherit', marginBottom: 12 }}
                    />
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {app.status === 'submitted' && (
                        <button type="button" disabled={updating} onClick={() => handleStatusUpdate('under_review')}
                          style={{ padding: '10px 16px', background: '#d97706', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: '0.86rem', cursor: 'pointer' }}>
                          🔍 पुनरावलोकनात घ्या (Under Review)
                        </button>
                      )}
                      {['submitted', 'under_review', 'documents_requested'].includes(app.status) && (
                        <button type="button" disabled={updating} onClick={() => handleStatusUpdate('approved')}
                          style={{ padding: '10px 16px', background: '#16a34a', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: '0.86rem', cursor: 'pointer' }}>
                          ✅ अर्ज मंजूर करा (Approve Application)
                        </button>
                      )}
                      {app.status === 'approved' && (
                        <button type="button" disabled={updating} onClick={() => handleStatusUpdate('issued')}
                          style={{ padding: '12px 16px', background: '#15803d', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 800, fontSize: '0.92rem', cursor: 'pointer', boxShadow: '0 2px 8px rgba(21,128,61,0.3)' }}>
                          📜 अधिकृत प्रमाणपत्र जारी करा (Issue Certificate)
                        </button>
                      )}
                      {['submitted', 'under_review', 'documents_requested'].includes(app.status) && (
                        <button type="button" disabled={updating} onClick={() => handleStatusUpdate('rejected')}
                          style={{ padding: '10px 16px', background: '#dc2626', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: '0.86rem', cursor: 'pointer' }}>
                          ❌ अर्ज नाकारा (Reject Application)
                        </button>
                      )}
                    </div>
                  </div>
                )}

                {/* Print & View Certificate Section for approved/issued */}
                {(app.status === 'issued' || app.status === 'approved') && (
                  <div style={{ background: '#fff', borderRadius: 10, padding: 16, border: '1.5px solid #86efac', textAlign: 'center' }}>
                    <div style={{ fontSize: '0.88rem', color: '#166534', fontWeight: 800, marginBottom: 8 }}>
                      🖨️ अधिकृत प्रमाणपत्र तयार झाले आहे!
                    </div>
                    {app.certificate_number && (
                      <div style={{ fontFamily: 'monospace', fontSize: '0.95rem', color: '#1e293b', fontWeight: 800, marginBottom: 12 }}>
                        {app.certificate_number}
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={() => setPreviewCertificateId(app.id)}
                      style={{ width: '100%', padding: '11px 18px', background: '#1a237e', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: '0.9rem', cursor: 'pointer', boxShadow: '0 2px 8px rgba(26,35,126,0.3)' }}
                    >
                      🖨️ प्रमाणपत्र पहा व प्रिंट करा
                    </button>
                    {app.status === 'issued' && canWriteThis && (
                      <button
                        type="button"
                        disabled={updating}
                        onClick={handleReissue}
                        style={{ width: '100%', marginTop: 9, padding: '9px 14px', background: '#fff', color: '#9a3412', border: '1px solid #fdba74', borderRadius: 8, fontWeight: 700, fontSize: '0.8rem', cursor: updating ? 'not-allowed' : 'pointer' }}
                      >
                        ↻ सध्याच्या प्रकाशित नमुन्याने पुनर्जारी करा
                      </button>
                    )}
                  </div>
                )}

              </div>

              {/* Review notes or rejection reason history */}
              {app.review_notes && (
                <div className="detail-card" style={{ background: '#fffbe6', border: '1px solid #ffe58f' }}>
                  <div style={{ fontSize: '0.78rem', color: '#d48806', fontWeight: 800, textTransform: 'uppercase', marginBottom: 4 }}>
                    📝 मागील पुनरावलोकन नोंद
                  </div>
                  <div style={{ fontSize: '0.88rem', color: '#451a03', lineHeight: 1.5 }}>{app.review_notes}</div>
                </div>
              )}
              {app.rejection_reason && (
                <div className="detail-card" style={{ background: '#fef2f2', border: '1px solid #fca5a5' }}>
                  <div style={{ fontSize: '0.78rem', color: '#991b1b', fontWeight: 800, textTransform: 'uppercase', marginBottom: 4 }}>
                    ❌ नाकारण्याचे कारण
                  </div>
                  <div style={{ fontSize: '0.88rem', color: '#7f1d1d', lineHeight: 1.5 }}>{app.rejection_reason}</div>
                </div>
              )}

            </div>

          </div>

        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="admin-card-header">
        <h2>📄 प्रमाणपत्र अर्ज</h2>
        {canWriteThis && (
          <button className="btn btn-primary" onClick={() => {
            if (showForm) resetApplicationForm();
            setShowForm(!showForm);
          }}>
            {showForm ? 'रद्द करा' : '+ नवीन अर्ज'}
          </button>
        )}
      </div>

      {success && <div className="alert alert-success">{success}</div>}
      {error && <div className="alert alert-error">{error}</div>}

      {showForm && (
        <div className="admin-card certificate-application-form" ref={certificateFormTopRef}>
          <form onSubmit={handleSubmit} noValidate>
            {serviceConfigLoading ? (
              <div className="certificate-form-state">प्रमाणपत्र फॉर्म संरचना लोड होत आहे…</div>
            ) : !certTypes.length ? (
              <div className="certificate-form-state error">ऑनलाइन अर्जासाठी कोणतीही सक्रिय ग्रामपंचायत प्रमाणपत्र सेवा उपलब्ध नाही.</div>
            ) : (
              <>
                <nav className="certificate-stepper" aria-label="प्रमाणपत्र अर्ज पायऱ्या">
                  {applicationSteps.map((step, index) => (
                    <button
                      type="button"
                      key={step.id}
                      className={`${index === certificateStep ? 'active' : ''} ${index < certificateStep ? 'completed' : ''}`}
                      onClick={() => goToCertificateStep(index)}
                      disabled={index > certificateStep}
                      aria-current={index === certificateStep ? 'step' : undefined}
                    >
                      <span>{index < certificateStep ? '✓' : index + 1}</span>
                      <strong>{step.title}</strong>
                    </button>
                  ))}
                </nav>

                {applicationSteps[certificateStep]?.id === 'applicant' && <section className="certificate-form-section certificate-step-panel">
                  <h3>अर्ज आणि अर्जदार माहिती</h3>
                  <div className="certificate-form-grid">
                    <div className="certificate-dynamic-field">
                      <label>प्रमाणपत्र प्रकार *</label>
                      <select
                        name="certificate_type"
                        value={formData.certificate_type}
                        aria-invalid={Boolean(formErrors.certificate_type)}
                        onChange={event => {
                          revokeAllPendingReceipts();
                          setFormData(current => ({ ...current, certificate_type: event.target.value }));
                          setDynamicData({});
                          clearDocumentPreviews();
                          setDocuments({});
                          setFormErrors({});
                          setCertificateStep(0);
                        }}
                      >
                        {certTypes.map(type => (
                          <option key={type} value={type}>{TYPE_LABELS[type] || type.replace(/_/g, ' ').toUpperCase()}</option>
                        ))}
                      </select>
                      {formErrors.certificate_type && <span className="certificate-field-error">{formErrors.certificate_type}</span>}
                    </div>
                    <div className="certificate-dynamic-field">
                      <label>अर्जदाराचे नाव *</label>
                      <input value={formData.applicant_name} aria-invalid={Boolean(formErrors.applicant_name)} onChange={event => setFormData(current => ({ ...current, applicant_name: event.target.value }))} />
                      {formErrors.applicant_name && <span className="certificate-field-error">{formErrors.applicant_name}</span>}
                    </div>
                    <div className="certificate-dynamic-field">
                      <label>फोन *</label>
                      <input data-marathi-input="false" lang="en" dir="ltr" inputMode="numeric" value={formData.applicant_phone} aria-invalid={Boolean(formErrors.applicant_phone)} onChange={event => setFormData(current => ({ ...current, applicant_phone: normalizePhoneInput(event.target.value) }))} maxLength="10" />
                      {formErrors.applicant_phone && <span className="certificate-field-error">{formErrors.applicant_phone}</span>}
                    </div>
                    <div className="certificate-dynamic-field">
                      <label>उद्देश</label>
                      <input value={formData.purpose} onChange={event => setFormData(current => ({ ...current, purpose: event.target.value }))} />
                    </div>
                    <div className="certificate-dynamic-field full-width">
                      <label>पत्ता</label>
                      <input value={formData.applicant_address} onChange={event => setFormData(current => ({ ...current, applicant_address: event.target.value }))} />
                    </div>
                  </div>
                </section>}

                {applicationSteps[certificateStep]?.section && (
                  <section className="certificate-form-section configured certificate-step-panel">
                    <h3>
                      <span>{applicationSteps[certificateStep].section.section_icon || '📌'}</span>
                      {applicationSteps[certificateStep].section.section_title}
                    </h3>
                    <div className="certificate-form-grid">
                      {(applicationSteps[certificateStep].section.fields || [])
                        .filter(field => isCertificateFieldVisible(field, resolvedDynamicData))
                        .map(field => (
                        <AdminDynamicField
                          key={field.key}
                          field={{
                            ...field,
                            required: isCertificateFieldRequired(field, resolvedDynamicData),
                            calculationHint: field.type === 'calculated' && field.calculation?.as_of_field && !resolvedDynamicData[field.calculation.as_of_field]
                              ? `हे मूल्य मोजण्यासाठी प्रथम ${dynamicFormSections.flatMap(section => section.fields || []).find(item => item.key === field.calculation.as_of_field)?.label || field.calculation.as_of_field} निवडा.`
                              : field.type === 'calculated' ? 'हे मूल्य संबंधित तारखांवरून आपोआप मोजले जाते.' : '',
                          }}
                          value={resolvedDynamicData[field.key]}
                          error={formErrors[field.key]}
                          onChange={value => {
                            const nextValue = field.key.includes('aadhaar')
                              ? toEnglishDigits(value).replace(/\D/g, '').slice(0, 12)
                              : value;
                            setDynamicData(current => ({ ...current, [field.key]: nextValue }));
                            setFormErrors(current => ({ ...current, [field.key]: '' }));
                          }}
                        />
                      ))}
                    </div>
                  </section>
                )}

                {applicationSteps[certificateStep]?.id === 'documents' && <section className="certificate-form-section certificate-step-panel">
                  <h3>📎 आवश्यक कागदपत्रे</h3>
                  {!requiredDocuments.length ? (
                    <p className="certificate-muted">या सेवेसाठी कागदपत्रे संरचित केलेली नाहीत.</p>
                  ) : (
                    <div className="certificate-document-grid">
                      {requiredDocuments.map(document => {
                        const uploaded = documents[document.name];
                        return (
                          <div className={`certificate-document-card ${uploaded?.url ? 'uploaded' : ''}`} key={document.name}>
                            <div>
                              <strong>{document.name}{document.is_required !== false && <b> *</b>}</strong>
                              <small>{uploaded?.originalName || 'JPG, PNG, PDF किंवा WebP — कमाल 5MB'}</small>
                              {uploaded?.error && <span className="certificate-field-error">{uploaded.error}</span>}
                            </div>
                            <input
                              ref={element => { documentInputRefs.current[document.name] = element; }}
                              type="file"
                              accept=".jpg,.jpeg,.png,.pdf,.webp"
                              hidden
                              onChange={event => handleConfiguredDocumentUpload(document.name, event.target.files?.[0])}
                            />
                            <div className="certificate-document-actions">
                              {uploaded?.url && (
                                <>
                                  {uploaded.mimeType?.startsWith('image/') && (
                                    <a className="btn btn-outline certificate-document-view" href={uploaded.previewUrl || getDocUrl(uploaded.url)} target="_blank" rel="noopener noreferrer">
                                      👁 पहा
                                    </a>
                                  )}
                                  <button type="button" className="btn btn-outline" onClick={() => downloadDocument(uploaded.previewUrl || getDocUrl(uploaded.url), uploaded.originalName || document.name)}>
                                    डाउनलोड ↓
                                  </button>
                                  <button type="button" className="btn btn-outline" onClick={() => removeConfiguredDocument(document.name)}>
                                    काढा
                                  </button>
                                </>
                              )}
                              <button type="button" className="btn btn-outline" disabled={uploaded?.uploading} onClick={() => documentInputRefs.current[document.name]?.click()}>
                                {uploaded?.uploading ? 'अपलोड होत आहे…' : uploaded?.url ? 'बदला' : 'अपलोड करा'}
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {formErrors.documents && <span className="certificate-field-error">{formErrors.documents}</span>}
                </section>}

                {applicationSteps[certificateStep]?.id === 'review' && <section className="certificate-form-section certificate-step-panel">
                  <h3>अतिरिक्त तपशील</h3>
                  <div className="certificate-dynamic-field">
                    <label>तपशील</label>
                    <textarea rows="3" value={formData.details} onChange={event => setFormData(current => ({ ...current, details: event.target.value }))} />
                  </div>
                  {selectedFee && (
                    <div className="certificate-service-summary">
                      <span>शुल्क: <strong>{selectedFee.contact_gp_for_fees ? 'शुल्क माहितीसाठी ग्रामपंचायतीशी संपर्क साधा' : (Number(selectedFee.fee_amount || 0) === 0 ? 'मोफत' : `₹${Number(selectedFee.fee_amount || 0).toFixed(2)}`)}</strong></span>
                      <span>प्रक्रिया कालावधी: <strong>{selectedFee.processing_time || 'संरचित नाही'}</strong></span>
                    </div>
                  )}
                  <div className="certificate-review-summary">
                    <div><span>प्रमाणपत्र</span><strong>{TYPE_LABELS[formData.certificate_type] || formData.certificate_type}</strong></div>
                    <div><span>अर्जदार</span><strong>{formData.applicant_name || '—'}</strong></div>
                    <div><span>मोबाइल</span><strong>{formData.applicant_phone || '—'}</strong></div>
                    <div><span>अपलोड केलेली कागदपत्रे</span><strong>{Object.values(documents).filter(document => document?.url).length}</strong></div>
                  </div>
                </section>}
              </>
            )}
            <div className="admin-form-actions certificate-step-actions">
              <div>
                <button type="button" className="btn btn-outline" onClick={() => { setShowForm(false); resetApplicationForm(); }}>रद्द करा</button>
              </div>
              <div>
                {certificateStep > 0 && (
                  <button type="button" className="btn btn-outline" onClick={() => goToCertificateStep(certificateStep - 1)}>← मागील पायरी</button>
                )}
                {certificateStep < applicationSteps.length - 1 ? (
                  <button type="button" className="btn btn-primary" disabled={serviceConfigLoading || !certTypes.length} onClick={() => goToCertificateStep(certificateStep + 1)}>
                    पुढील पायरी →
                  </button>
                ) : (
                  <button type="submit" className="btn btn-primary" disabled={submittingApplication || serviceConfigLoading || !certTypes.length}>
                    {submittingApplication ? 'अर्ज सादर होत आहे…' : 'अर्ज सादर करा'}
                  </button>
                )}
              </div>
            </div>
          </form>
        </div>
      )}

      {!showForm && (
        <div className="admin-card">
            <div className="certificate-table-toolbar">
              <div className="certificate-search"><span>⌕</span><input placeholder="UID किंवा अर्जदाराच्या नावाने शोधा..." value={search} onChange={e => handleSearchChange(e.target.value)} /></div>
              <select value={typeFilter} onChange={e => handleTypeFilterChange(e.target.value)}>
                <option value="">सर्व प्रकार</option>
                {filterCertTypes.map(t => <option key={t} value={t}>{TYPE_LABELS[t] || t.replace(/_/g, ' ').toUpperCase()}</option>)}
              </select>
              <select value={statusFilter} onChange={e => handleStatusFilterChange(e.target.value)}>
                <option value="">सर्व स्थिती</option>
                <option value="submitted"> सादर</option>
                <option value="under_review">पुनरावलोकनात</option>
                <option value="approved">मंजूर</option>
                <option value="rejected">नाकारले</option>
                <option value="issued">जारी</option>
              </select>
              <button className="certificate-export-btn" onClick={exportCsv} disabled={exporting}>⇩ {exporting ? 'तयार होत आहे...' : 'CSV निर्यात'}</button>
            </div>
            <div className="certificate-table-meta"><span><strong>{totalCount}</strong> प्रमाणपत्र अर्ज</span><span className={`certificate-load-status ${loading ? 'visible' : ''}`}>{loading && <><i /> माहिती अपडेट होत आहे</>}</span></div>
            <div className={`table-scroll certificate-table-scroll ${loading ? 'is-refreshing' : ''}`}>
              <table className="data-table certificate-data-table">
                <thead><tr><CertificateSortHeader label="UID" column="application_uid" sort={sort} onSort={toggleSort}/><CertificateSortHeader label="प्रकार" column="certificate_type" sort={sort} onSort={toggleSort}/><CertificateSortHeader label="अर्जदार" column="subject_name" sort={sort} onSort={toggleSort}/><CertificateSortHeader label="स्थिती" column="status" sort={sort} onSort={toggleSort}/><CertificateSortHeader label="शुल्क" column="fee_amount" sort={sort} onSort={toggleSort}/><CertificateSortHeader label="तारीख" column="submitted_at" sort={sort} onSort={toggleSort}/><th>कृती</th></tr></thead>
                <tbody>
                  {loading && certs.length === 0 ? (
                    <LoadingSpinner.TableRow colSpan={7} message="प्रमाणपत्र अर्ज लोड होत आहेत..." />
                  ) : certs.length === 0 ? (
                    <tr><td colSpan="7" style={{ textAlign: 'center', padding: '24px', color: '#64748b' }}>प्रमाणपत्र अर्ज आढळले नाहीत.</td></tr>
                  ) : (
                    certs.map((c) => (
                    <tr key={c.id} style={{ cursor: 'pointer' }} onClick={() => openDetail(c.id)}>
                      <td><span className="certificate-uid">{c.application_uid}</span></td>
                      <td style={{ textTransform: 'capitalize' }}>{TYPE_LABELS[c.certificate_type] || c.certificate_type?.replace(/_/g, ' ')}</td>
                      <td><div className="certificate-applicant"><span>{(c.applicant?.full_name || c.subject_name || '?').charAt(0)}</span><strong>{c.applicant?.full_name || c.subject_name || '-'}</strong></div></td>
                      <td><span className={`certificate-status status-${c.status}`}>{STATUS_LABELS[c.status] || c.status}</span></td>
                      <td>₹{c.fee_amount || 0}</td>
                      <td>{new Date(c.createdAt).toLocaleDateString('mr-IN')}</td>
                      <td>
                        <button className="certificate-view-btn" onClick={(e) => { e.stopPropagation(); openDetail(c.id); }}>
                          तपशील <span>›</span>
                        </button>
                      </td>
                    </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            <div className="table-pagination certificate-pagination">
              <label>प्रति पृष्ठ <select value={pageSize} onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}><option value="10">10</option><option value="20">20</option><option value="50">50</option></select></label>
              <button className="btn btn-outline btn-sm" onClick={() => setPage(p => Math.max(1, p-1))} disabled={page === 1}>&#8592; मागील</button>
              <span>पृष्ठ <strong>{page}</strong> / {totalPages}</span>
              <button className="btn btn-outline btn-sm" onClick={() => setPage(p => Math.min(totalPages, p+1))} disabled={page === totalPages}>पुढील &#8594;</button>
            </div>
        </div>
      )}
    </div>
  );
}

function CertificateSortHeader({ label, column, sort, onSort }) { const active = sort.key === column; return <th><button className={`certificate-sort-header ${active ? 'active' : ''}`} onClick={() => onSort(column)}>{label}<span>{active ? (sort.direction === 'ASC' ? '↑' : '↓') : '↕'}</span></button></th>; }
